import { useConvexAction, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route as JobsRoute } from '@/routes/_app/_auth/dashboard/_layout.jobs.index'
import { GenerateButton } from '@/shared/ui/generate-button'

export function CreateJobPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { mutateAsync: createJob } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.createMyJob),
  })

  const {
    mutateAsync: generateDescription,
    isPending: isGeneratingDescription,
  } = useMutation({
    mutationFn: useConvexAction(
      api.features.jobs.generate_text_fields.generateMyJobDescription,
    ),
  })

  const form = useForm({
    defaultValues: {
      title: '',
      description: '',
      language: 'sv' as 'sv' | 'en' | 'both' | 'no' | 'da' | 'fi',
      allowedModalities: ['audio'] as ('video' | 'audio' | 'chat')[],
      expiryDays: 14,
      interviewCount: 1,
      topNRecommendationCount: 3,
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true)
      await createJob(value)
      setIsSubmitting(false)
      navigate({ to: JobsRoute.fullPath })
    },
  })

  return (
    <div className="flex h-full w-full px-6 py-8">
      <div className="mx-auto flex h-full w-full max-w-(--breakpoint-xl) flex-col gap-6">
        <form
          className="flex w-full max-w-2xl flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-primary">
              {t('jobs.form.titleLabel')}
            </label>
            <form.Field
              name="title"
              children={(field) => (
                <Input
                  placeholder={t('jobs.form.titlePlaceholder')}
                  required
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="bg-transparent"
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-primary">
                {t('jobs.form.descriptionLabel')}
              </label>
              <form.Subscribe
                selector={(state) => ({
                  title: state.values.title,
                  language: state.values.language,
                })}
                children={({ title, language }) => (
                  <GenerateButton
                    isPending={isGeneratingDescription}
                    disabled={!title.trim()}
                    onClick={() =>
                      void generateDescription({ title, language }).then(
                        (html) => form.setFieldValue('description', html),
                      )
                    }
                  />
                )}
              />
            </div>
            <form.Field
              name="description"
              children={(field) => (
                <textarea
                  placeholder={t('jobs.form.descriptionPlaceholder')}
                  required
                  rows={5}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                />
              )}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-primary">
              {t('jobs.form.languageLabel')}
            </label>
            <form.Field
              name="language"
              children={(field) => (
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(
                      v as 'sv' | 'en' | 'both' | 'no' | 'da' | 'fi',
                    )
                  }
                >
                  <SelectTrigger className="w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sv">
                      {t('jobs.form.languageSv')}
                    </SelectItem>
                    <SelectItem value="en">
                      {t('jobs.form.languageEn')}
                    </SelectItem>
                    <SelectItem value="no">
                      {t('jobs.form.languageNo')}
                    </SelectItem>
                    <SelectItem value="da">
                      {t('jobs.form.languageDa')}
                    </SelectItem>
                    <SelectItem value="fi">
                      {t('jobs.form.languageFi')}
                    </SelectItem>
                    <SelectItem value="both">
                      {t('jobs.form.languageBoth')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-primary">
              {t('jobs.form.allowedModalitiesLabel')}
            </label>
            <form.Field
              name="allowedModalities"
              children={(field) => (
                <div className="flex gap-4">
                  {(['video', 'audio', 'chat'] as const).map((modality) => (
                    <label
                      key={modality}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={field.state.value.includes(modality)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...field.state.value, modality]
                            : field.state.value.filter((m) => m !== modality)
                          field.handleChange(next)
                        }}
                      />
                      {modality}
                    </label>
                  ))}
                </div>
              )}
            />
          </div>

          <div className="flex gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-primary">
                {t('jobs.form.expiryDaysLabel')}
              </label>
              <form.Field
                name="expiryDays"
                children={(field) => (
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    required
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(Number(e.target.value))}
                    className="w-32 bg-transparent"
                  />
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-primary">
                {t('jobs.form.interviewCountLabel')}
              </label>
              <form.Field
                name="interviewCount"
                children={(field) => (
                  <Input
                    type="number"
                    min={1}
                    required
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(Number(e.target.value))}
                    className="w-32 bg-transparent"
                  />
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-primary">
                {t('jobs.form.topNLabel')}
              </label>
              <form.Field
                name="topNRecommendationCount"
                children={(field) => (
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    required
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(Number(e.target.value))}
                    className="w-32 bg-transparent"
                  />
                )}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: JobsRoute.fullPath })}
            >
              {t('jobs.form.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="animate-spin" />
              ) : (
                t('jobs.form.create')
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
